// İSG / Süper Admin: Çalışma izinleri takvimi (durum çipleri + lokasyon, firma, özel durum filtresi)
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Check } from 'lucide-react';
import { HAZARD, HAZARD_OPTIONS } from './wizard/HazardClassPicker';
import { TodayOnSite, HazardTag, no, jobType } from './TodayOnSite';
import { WeekView, MonthView, agendaStyle, iso, useCalendarRange, CalendarToolbar } from '../settings/calendarViews';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const CAL_STATUSES = [
  ['aktif', 'Onaylı', '#16a34a'], ['isg_inceleme', 'İncelemede', '#0284c7'], ['onay_bekliyor', '2. Onay Bekliyor', '#4f46e5'],
  ['eksik_hatali', 'Eksik/Hatalı', '#ea580c'], ['talep_bilgisi_hatali', 'Talep Bilgisi Hatalı', '#e11d48'], ['suresi_dolmus', 'Süresi Biten', '#64748b'],
  ['red_edildi', 'Reddedildi', '#b91c1c'], ['geri_cekildi', 'Geri Çekildi', '#94a3b8'],
];
const COLOR = Object.fromEntries(CAL_STATUSES.map(([k, , c]) => [k, c]));
const LABEL = Object.fromEntries(CAL_STATUSES.map(([k, l]) => [k, l]));
// Mobil kartlarla aynı: pastel zemin, koyu yazı, solda durum çizgisi
const styleOf = agendaStyle;
const weekLines = (ev) => [
  <><HazardTag ev={ev} />{`${no(ev)} · ${ev.start_time}–${ev.end_time} · ${ev.location_name}`}</>,
  `${jobType(ev)} · ${ev.employee_count} kişi`,
  ev.equipment_text,
  ev.job_description,
  ev.company_name,
];
const monthLines = (ev) => [<><HazardTag ev={ev} />{`${ev.start_time} ${no(ev)} · ${ev.location_name}`}</>, `${jobType(ev)} · ${ev.employee_count} kişi`];
const fmt = (d) => (d ? `${d.slice(8, 10)}.${d.slice(5, 7)}.${d.slice(0, 4)}` : '-');
const Row = ({ k, v }) => <p className="flex gap-2"><span className="w-24 shrink-0 text-slate-400">{k}</span><span className="font-medium text-slate-800 break-words">{v || '-'}</span></p>;
const tip = (ev) => (
  <div className="space-y-1 p-3 text-xs bg-white text-slate-700 rounded-md">
    <div className="flex items-center justify-between gap-3 pb-1 mb-1 border-b">
      <span className="text-sm font-semibold text-slate-900">Talep {no(ev)}</span>
      <span className="rounded-full px-2 py-0.5 text-[10px] font-medium text-white" style={{ backgroundColor: COLOR[ev.display_status] }}>{LABEL[ev.display_status]}</span>
    </div>
    <Row k="Tehlike Sınıfı" v={[HAZARD[ev.hazard_class]?.label || 'Belirtilmemiş', ev.equipment_hazard_class && `Ekipman firması: ${HAZARD[ev.equipment_hazard_class]?.label}`].filter(Boolean).join(' · ')} />
    <Row k="Lokasyon" v={ev.location_name} />
    <Row k="Firma" v={ev.company_name + (ev.on_behalf_of ? ` (${ev.on_behalf_of} adına)` : '')} />
    <Row k="Tarih / Saat" v={`${fmt(ev.start_date)} – ${fmt(ev.end_date)} · ${ev.start_time}–${ev.end_time}`} />
    <Row k="İş Tipi" v={ev.is_tipi} />
    <Row k="İş Çeşidi" v={ev.is_cesidi} />
    <Row k="İş Ekipmanı" v={ev.equipment_text} />
    <Row k="Personel" v={`${ev.employee_count} kişi`} />
    <Row k="İş Tanımı" v={ev.job_description} />
    {ev.special_situations?.length > 0 && <Row k="Özel Durum" v={ev.special_situations.map(s => s.title).join(', ')} />}
    <p className="pt-1 text-[10px] text-slate-400">Detay için tıklayın</p>
  </div>
);

const StatusChips = ({ value, onChange }) => (
  <div className="flex flex-wrap gap-1.5" data-testid="calendar-status-chips">
    {CAL_STATUSES.map(([k, l, c]) => {
      const on = value.includes(k);
      return (
        <button key={k} type="button" onClick={() => onChange(on ? value.filter(x => x !== k) : [...value, k])} data-testid={`calendar-status-${k}`} aria-pressed={on}
          className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${on ? 'text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
          style={on ? { backgroundColor: c, borderColor: c } : { borderColor: '#e2e8f0' }}>
          {on ? <Check className="h-3 w-3" /> : <span className="h-2 w-2 rounded-full" style={{ backgroundColor: c }} />}{l}
        </button>
      );
    })}
  </div>
);

export const PermitsCalendar = ({ locations, companies, onView }) => {
  const [view, setView] = useState('week');
  const [anchor, setAnchor] = useState(new Date());
  const [statuses, setStatuses] = useState(['aktif', 'isg_inceleme']);
  const [locationId, setLocationId] = useState('all');
  const [companyId, setCompanyId] = useState('all');
  const [situationId, setSituationId] = useState('all');
  const [hazardId, setHazardId] = useState('all');
  const [events, setEvents] = useState([]);
  const days = useCalendarRange(view, anchor);

  useEffect(() => {
    if (!statuses.length) { setEvents([]); return; }
    const params = { start: days[0], end: days[days.length - 1], statuses: statuses.join(',') };
    if (locationId !== 'all') params.location_id = locationId;
    if (companyId !== 'all') params.company_id = companyId;
    if (hazardId !== 'all') params.hazard_class = hazardId;
    axios.get(`${API}/work-permits/calendar`, { params }).then(r => setEvents(r.data)).catch(() => setEvents([]));
  }, [days, statuses, locationId, companyId, hazardId]);

  const situations = useMemo(() => {
    const m = new Map();
    events.forEach(ev => (ev.special_situations || []).forEach(s => m.set(s.id, s.title)));
    return [...m.entries()];
  }, [events]);
  const shown = situationId === 'all' ? events : events.filter(ev => (ev.special_situations || []).some(s => s.id === situationId));
  const props = { days, events: shown, colorOf: (ev) => COLOR[ev.display_status] || '#64748b', onPick: onView, today: iso(new Date()), styleOf, weekLines, monthLines, tip };

  return (
    <div className="space-y-4" data-testid="permits-calendar">
      <CalendarToolbar view={view} setView={setView} anchor={anchor} setAnchor={setAnchor} days={days}>
        <Select value={locationId} onValueChange={setLocationId}>
          <SelectTrigger className="w-full sm:w-48" data-testid="permits-calendar-location-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tüm lokasyonlar</SelectItem>{locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={companyId} onValueChange={setCompanyId}>
          <SelectTrigger className="w-full sm:w-48" data-testid="permits-calendar-company-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tüm firmalar</SelectItem>{companies.filter(c => !c.parent_company_id).map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={hazardId} onValueChange={setHazardId}>
          <SelectTrigger className="w-full sm:w-44" data-testid="permits-calendar-hazard-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm tehlike sınıfları</SelectItem>
            {HAZARD_OPTIONS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
            <SelectItem value="none">Belirtilmemiş</SelectItem>
          </SelectContent>
        </Select>
        <Select value={situationId} onValueChange={setSituationId}>
          <SelectTrigger className="w-full sm:w-48" data-testid="permits-calendar-situation-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tüm özel durumlar</SelectItem>{situations.map(([id, t]) => <SelectItem key={id} value={id}>{t}</SelectItem>)}</SelectContent>
        </Select>
      </CalendarToolbar>
      <TodayOnSite onView={onView} situationId={situationId} filters={{
        ...(locationId !== 'all' && { location_id: locationId }), ...(companyId !== 'all' && { company_id: companyId }), ...(hazardId !== 'all' && { hazard_class: hazardId }),
      }} />
      <div className="flex flex-wrap items-center gap-3">
        <StatusChips value={statuses} onChange={setStatuses} />
        <span className="ml-auto text-xs text-slate-400" data-testid="permits-calendar-count">{shown.length} talep</span>
      </div>
      {view === 'week' ? <WeekView {...props} /> : <MonthView {...props} month={anchor.getMonth()} />}
    </div>
  );
};
